import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type ReactNode, type RefObject, useMemo, useRef, useState } from 'react';
import { api, type FacetsResponse } from '@/api/client';
import { compactCount } from '@/lib/format';
import { applySuggestion, isIncomplete, suggest, tokenAt } from '@/lib/suggest';
import { QueryHighlight } from '@/lib/text';
import { Icon } from './Icon';

export interface QueryBarProps {
    input: string;
    onInput: (q: string) => void;
    // Runs the query now (Enter, a recent query, an example).
    onRun: (q: string) => void;
    error: string | null;
    facets: FacetsResponse | undefined;
    // Where the values come from: the environment and the time range on screen.
    scope: { env: string; from: number; to: number };
    envs: readonly string[];
    recent: readonly string[];
    inputRef: RefObject<HTMLInputElement | null>;
    // Inside the bar, at its end: the time range.
    trailing?: ReactNode;
}

const EXAMPLES: ReadonlyArray<[string, string]> = [
    ['timeout', 'free text, in the message and the attributes'],
    ['"payment failed"', 'a phrase'],
    ['level:>=warn -kind:request', 'warnings and worse, no request dumps'],
    ['service:api status:>=500', 'server errors of the API'],
    ['module:"Cron Job" duration:>1000', 'slow cron runs'],
    ['(timeout OR reset) service:api', 'either word, grouped'],
    ['logger:*Queue*', 'a wildcard'],
    ['/time(d)? ?out/', 'a regular expression'],
    ['has:user exception:AxiosError', 'a field that is set'],
    ['request.headers.cf-ipcountry:DE', 'any attribute, by its path'],
];

// The query bar: the query coloured as it parses, completions for fields and their values, recent queries, examples;
// the time range sits at its end.
export function QueryBar({ inputRef, ...props }: QueryBarProps): ReactNode {
    const highlight = useRef<HTMLDivElement>(null);
    const [caret, setCaret] = useState(0);
    const [open, setOpen] = useState(false);
    const [index, setIndex] = useState(-1);
    const [help, setHelp] = useState(false);
    // A value is completed from the facets of the query without the token being typed: `service:` offers every service
    // the rest of the query finds, not only those of what is half-typed.
    const typing = tokenAt(props.input, caret);
    const valueMode = typing.text.replace(/^-/, '').includes(':');
    const rest = (props.input.slice(0, typing.start) + props.input.slice(typing.end)).replace(/\s+/g, ' ').trim();
    const contextScope = { ...props.scope, q: rest };
    const contextFacets = useQuery({
        queryKey: ['facets', contextScope],
        queryFn: ({ signal }) => api.facets(contextScope, signal),
        enabled: open && valueMode && !isIncomplete(rest),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });
    const facets = valueMode ? (contextFacets.data ?? props.facets) : props.facets;
    const { items, token } = useMemo(
        () => suggest(props.input, caret, { facets, envs: props.envs, recent: props.recent }),
        [props.input, caret, facets, props.envs, props.recent],
    );
    const showList = open && !help && items.length > 0;

    const sync = (): void => {
        if (highlight.current && inputRef.current) {
            highlight.current.scrollLeft = inputRef.current.scrollLeft;
            setCaret(inputRef.current.selectionStart ?? inputRef.current.value.length);
        }
    };
    const accept = (position: number): void => {
        const item = items[position];
        if (!item) {
            return;
        }
        if (item.kind === 'recent') {
            props.onInput(item.insert);
            props.onRun(item.insert);
            setOpen(false);

            return;
        }
        const next = applySuggestion(props.input, token, item.insert);
        props.onInput(next.text);
        setIndex(-1);
        requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(next.caret, next.caret);
            sync();
        });
    };

    return (
        <div className={`qb ${props.error ? 'invalid' : ''} ${showList || help ? 'open' : ''}`}>
            <span className="icon">
                <Icon name="search" size={18} />
            </span>
            <div className="qb-wrap">
                <div className="qb-hl" ref={highlight}>
                    <QueryHighlight q={props.input} />
                </div>
                <input
                    ref={inputRef}
                    value={props.input}
                    spellCheck={false}
                    autoComplete="off"
                    placeholder='Search the lines, or filter: service:api  level:>=warn  "a phrase"  -exclude'
                    onChange={(event) => {
                        props.onInput(event.target.value);
                        setOpen(true);
                        setIndex(-1);
                        requestAnimationFrame(sync);
                    }}
                    onFocus={() => {
                        setOpen(true);
                        requestAnimationFrame(sync);
                    }}
                    onBlur={() => setOpen(false)}
                    onClick={sync}
                    onScroll={sync}
                    onKeyUp={(event) => {
                        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Home' || event.key === 'End') {
                            sync();
                        }
                    }}
                    onKeyDown={(event) => {
                        if (showList && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
                            event.preventDefault();
                            const down = event.key === 'ArrowDown';
                            setIndex((current) => (down ? (current + 1) % items.length : current <= 0 ? items.length - 1 : current - 1));

                            return;
                        }
                        if (showList && event.key === 'Tab') {
                            event.preventDefault();
                            accept(Math.max(0, index));

                            return;
                        }
                        if (event.key === 'Enter') {
                            if (showList && index >= 0) {
                                accept(index);
                            } else {
                                props.onRun(props.input);
                                setOpen(false);
                            }

                            return;
                        }
                        if (event.key === 'Escape') {
                            if (showList || help) {
                                setOpen(false);
                                setHelp(false);
                            } else {
                                event.currentTarget.blur();
                            }
                        }
                    }}
                />
            </div>
            {props.input && (
                <button
                    className="qb-clear"
                    title="Clear the query"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                        props.onInput('');
                        props.onRun('');
                        inputRef.current?.focus();
                    }}
                >
                    <Icon name="x" size={15} />
                </button>
            )}
            <button
                className={`qb-help ${help ? 'on' : ''}`}
                title="How to search"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setHelp(!help)}
            >
                <Icon name="help" size={17} />
            </button>
            {props.trailing && (
                <>
                    <span className="vsep" />
                    {props.trailing}
                </>
            )}
            {props.error && <div className="qb-error">{props.error}</div>}
            {showList && (
                <div className="qb-pop float" onMouseDown={(event) => event.preventDefault()}>
                    {items[0]?.kind === 'recent' && <div className="pg">Recent</div>}
                    {items.map((item, position) => (
                        <button
                            key={`${item.kind}-${item.insert}`}
                            className={`qs ${item.kind} ${position === index ? 'on' : ''}`}
                            onMouseEnter={() => setIndex(position)}
                            onClick={() => accept(position)}
                        >
                            <span className="ql">{item.kind === 'recent' ? <QueryHighlight q={item.label} /> : item.label}</span>
                            {item.detail && <span className="qd">{item.detail}</span>}
                            {item.count !== undefined && <span className="qc">{compactCount(item.count)}</span>}
                        </button>
                    ))}
                    <div className="qb-keys">
                        <span>
                            <span className="kbd">↑↓</span> choose
                        </span>
                        <span>
                            <span className="kbd">Tab</span> complete
                        </span>
                        <span>
                            <span className="kbd">↵</span> run
                        </span>
                    </div>
                </div>
            )}
            {help && (
                <div className="qb-pop help float" onMouseDown={(event) => event.preventDefault()}>
                    <div className="pg">How to search · click an example to run it</div>
                    {EXAMPLES.map(([query, meaning]) => (
                        <button
                            key={query}
                            className="qs"
                            onClick={() => {
                                props.onInput(query);
                                props.onRun(query);
                                setHelp(false);
                            }}
                        >
                            <span className="ql">
                                <QueryHighlight q={query} />
                            </span>
                            <span className="qd">{meaning}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
